import {ApiResponseDto, BasePopupProps} from "../../../../utils/Models";
import FlintLoadingWrapper from "../../../../components/page_components/common/loading/FlintLoadingWrapper";
import FlintDateTimePicker from "../../../../components/controls/FlintDateTimePicker";
import {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import FlintLabelBox from "../../../../components/controls/FlintLabelBox";
import {useOrderPreviewContext} from "../OrderPreviewContext";
import {parseISO} from "date-fns";
import {toString as toKendoString} from "@progress/kendo-intl"
import FlintTextArea from "../../../../components/controls/FlintTextArea";
import {WindowActionsBar} from "@progress/kendo-react-dialogs";
import FlintButton from "../../../../components/controls/FlintButton";
import {
    AssignProjectLeaderRequest,
    OrderPreviewBasicDataModel,
    OrderPreviewModelStateType,
    StartWorkRequest
} from "../../order.models";
import axios from "axios";
import {ApiUrl_Orders_UpdateConfirmedDate, ApiUrl_Orders_UpdateProjectLeader} from "../../../../utils/Endpoints";
import {
    useFlintNotificationContext
} from "../../../../components/page_components/common/notifications/FlintNotificationContext";
import {TextAreaChangeEvent} from "@progress/kendo-react-inputs";

export default function UpdateConfirmedDateComponent(props: UpdateConfirmedDateComponentProps) {

    const {t} = useTranslation();
    const {state, handlers, utils} = useUpdateConfirmedDateComponent(props);
    return (
        <>
            <FlintLoadingWrapper showLoading={state.isLoading.get}>
                <div className={"form-group"}>
                    <FlintLabelBox title={t("ORDER_DATE_REQUESTED")}
                                   className={"d-flex justify-content-between align-items-center"}
                                   text={toKendoString(parseISO(state.basicData.get.DueDateTimeUtc + 'Z'), "yyyy-MM-dd HH:mm")}/>
                </div>
                <div className={"form-group"}>
                    <FlintDateTimePicker label={t("lblConfirmedDate")} value={state.confirmedDate.get}
                                         onChange={x => handlers.handleConfirmedDateUpdated(x.value)}/>
                </div>
                {state.delayReasonVisible.get && <div className={"form-group"}>
                    <div className={"form-group"}>
                        <FlintTextArea label={t("lblDelayReasonPrompt")} maxLength={1000} rows={3}
                                       value={state.delayReason.get} onChange={handlers.handleDelayReasonChanged}/>
                    </div>
                </div>}
            </FlintLoadingWrapper>
            <WindowActionsBar layout={"center"}>
                <FlintButton showLoadingIcon={state.isLoading.get} disabled={state.isLoading.get}
                             themeColor={"secondary"}
                             onClick={handlers.handleCancelled}>{t("Cancel")}</FlintButton>
                <FlintButton showLoadingIcon={state.isLoading.get} disabled={state.isLoading.get}
                             themeColor={"primary"}
                             onClick={handlers.handleConfirmed}>{t("Confirm")}</FlintButton>
            </WindowActionsBar>
        </>)
}

export interface UpdateConfirmedDateComponentProps extends BasePopupProps {
    orderPreviewModelState: OrderPreviewModelStateType
}

function useUpdateConfirmedDateComponent(props: UpdateConfirmedDateComponentProps) {
    const [isLoading, setIsLoading] = useState(false);
    const {showNotification} = useFlintNotificationContext();
    const basicDataState = props.orderPreviewModelState.get.OrderBasicData;
    const [selectedConfirmedDate, setSelectedConfirmedDate] = useState<Date>(parseISO(basicDataState.ConfirmedDateUtc + 'Z'))
    const [delayReason, setDelayReason] = useState<string>(basicDataState.DelayReason);
    const [delayReasonVisible, setDelayReasonVisible] = useState<boolean>(false);

    const requestedDateTime = useMemo(() => parseISO(basicDataState.DueDateTimeUtc + 'Z'), []);
    useEffect(() => {
        setDelayReasonVisible(selectedConfirmedDate != null && requestedDateTime < selectedConfirmedDate);
        if (!delayReasonVisible && delayReason)
            setDelayReason("");
    }, [selectedConfirmedDate])

    function handleConfirmedDateUpdated(date: Date | null): void {
        if (!date)
            return;
        setSelectedConfirmedDate(date);
    }

    function handleDelayReasonChanged(event: TextAreaChangeEvent) {
        setDelayReason(event.value);
    }

    function handleCancelled() {
        props.OnCancelled();
    }

    function handleConfirmed() {
        const post: StartWorkRequest = {
            OrderGuid: basicDataState.Guid,
            ConfirmedDateUtc: selectedConfirmedDate,
            DelayReason: delayReason
        }
        setIsLoading(true);
        axios.post<ApiResponseDto<OrderPreviewBasicDataModel>>(ApiUrl_Orders_UpdateConfirmedDate, post)
            .then(res => {
                props.orderPreviewModelState.set("OrderBasicData", res.data.Result);
                showNotification("Confirmed date has been successfully updated", {style: "success"});
                props.OnConfirmedAndSaved();
            })
            .catch(err => showNotification(err))
            .finally(() => setIsLoading(false))
    }

    return {
        handlers: {handleConfirmedDateUpdated, handleCancelled, handleConfirmed, handleDelayReasonChanged},
        state: {
            isLoading: {get: isLoading},
            basicData: {get: basicDataState},
            confirmedDate: {get: selectedConfirmedDate},
            delayReason: {get: delayReason},
            delayReasonVisible: {get: delayReasonVisible},
        },
        utils: {}
    }
}